import { FileDownload as DownloadIcon, KeyboardArrowDown as ArrowDownIcon } from '@mui/icons-material';
import { Box, IconButton, MenuItem, Select, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { toPng } from 'html-to-image';
import { lazy, Suspense, useCallback, useRef, useState } from 'react';

// Reload the page once if a lazy chunk fails to fetch (stale asset after deployment)
function lazyWithRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch {
      window.location.reload();
      throw new Error('Reloading to fetch updated assets');
    }
  });
}

const AffordabilityCalculator = lazyWithRetry(
  () => import('@/components/dashboard/calculators/AffordabilityCalculator'),
);
const HELOCCalculator = lazyWithRetry(
  () => import('@/components/dashboard/calculators/HELOCCalculator'),
);
const MortgageCalculator = lazyWithRetry(
  () => import('@/components/dashboard/calculators/MortgageCalculator'),
);
const RefinanceCalculator = lazyWithRetry(
  () => import('@/components/dashboard/calculators/RefinanceCalculator'),
);
const RentVsBuyCalculator = lazyWithRetry(
  () => import('@/components/dashboard/calculators/RentVsBuyCalculator'),
);
const CompoundInterestCalculator = lazyWithRetry(
  () => import('@/components/dashboard/calculators/CompoundInterestCalculator'),
);
const ClosingCostCalculator = lazyWithRetry(
  () => import('@/components/dashboard/calculators/ClosingCostCalculator'),
);

const C = {
  card: 'var(--tx-glass-panel)',
  text: 'var(--tx-text)',
  muted: 'var(--tx-muted)',
  subtle: 'var(--tx-faint)',
};

const font = "'Manrope', sans-serif";

const TABS = [
  {
    id: 'closing-costs',
    label: 'Closing Costs',
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M9 5H2v7l6.29 6.29a1 1 0 0 0 1.42 0l5.58-5.58a1 1 0 0 0 0-1.42L9 5Z" />
        <circle cx="6" cy="9" r="1" fill="currentColor" />
        <path d="M15 5l5.29 5.29a1 1 0 0 1 0 1.42L14 18" />
      </svg>
    ),
  },
  {
    id: 'mortgage',
    label: 'Mortgage',
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    id: 'affordability',
    label: 'Affordability',
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
  },
  {
    id: 'rent-vs-buy',
    label: 'Rent vs. Buy',
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <path d="M9 22V12h6v10" />
        <path d="M14 2l4 4M10 2L6 6" />
      </svg>
    ),
  },
  {
    id: 'refinance',
    label: 'Refinance',
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10" />
        <polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
      </svg>
    ),
  },
  {
    id: 'heloc',
    label: 'HELOC',
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M15 11h-2.5a1.5 1.5 0 0 0 0 3h1a1.5 1.5 0 0 1 0 3H11" />
      </svg>
    ),
  },
  {
    id: 'compound-interest',
    label: 'Comp. Interest',
    mobileLabel: 'Compound Interest',
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round">
        <rect x="3" y="16" width="4" height="6" rx="1" />
        <rect x="10" y="10" width="4" height="12" rx="1" />
        <rect x="17" y="4" width="4" height="18" rx="1" />
      </svg>
    ),
  },
];

const PlaceholderContent = ({ tabId }: { tabId: string }) => {
  const tab = TABS.find((t) => t.id === tabId);
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 300,
        gap: 2,
        color: C.subtle,
        fontFamily: font,
      }}>
      <Box sx={{ opacity: 0.4, transform: 'scale(2.5)', mb: 2 }}>{tab?.icon}</Box>
      <Typography sx={{ fontSize: 16, fontWeight: 600, color: C.muted, fontFamily: font }}>
        {tab?.label} Calculator
      </Typography>
      <Typography sx={{ fontSize: 13, color: C.subtle, fontFamily: font }}>Coming soon</Typography>
    </Box>
  );
};

export const CalculatorsView = () => {
  const [tab, setTab] = useState('closing-costs');
  const [snapping, setSnapping] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const scrollRef = useRef<HTMLDivElement>(null);
  const calculatorRef = useRef<HTMLDivElement>(null);

  const handleSnapshot = useCallback(async () => {
    if (!calculatorRef.current || snapping) return;
    setSnapping(true);
    try {
      const el = calculatorRef.current;
      const dataUrl = await toPng(el, {
        cacheBust: true,
        pixelRatio: 2,
        width: el.scrollWidth,
        height: el.scrollHeight,
        style: { overflow: 'visible', height: `${el.scrollHeight}px` },
      });
      const a = document.createElement('a');
      a.download = `hauser-${tab}.png`;
      a.href = dataUrl;
      a.click();
    } finally {
      setSnapping(false);
    }
  }, [snapping, tab]);

  return (
    <Box
      ref={scrollRef}
      data-testid="calculators-view"
      sx={{
        fontFamily: font,
        padding: isMobile ? '0' : '20px 24px',
        position: 'relative',
        minHeight: '100%',
      }}>
      {/* Tab selector */}
      {isMobile ? (
        /* Mobile: dropdown selector */
        <Box sx={{ mb: '10px', px: '10px' }}>
          <Select
            value={tab}
            onChange={(e) => setTab(e.target.value)}
            variant="standard"
            disableUnderline
            IconComponent={ArrowDownIcon}
            sx={{
              // a slate tile like the deal selector: it sits on the chalk ground, where a translucent fill washes out
              width: '100%',
              backgroundColor: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow-sm)',
              borderRadius: '16px',
              fontFamily: font,
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--tx-text)',
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 20px !important',
                paddingRight: '44px !important',
                fontSize: 14,
              },
              '& .MuiSelect-icon': {
                color: C.muted,
                right: 18,
                top: '50%',
                transform: 'translateY(-50%)',
              },
            }}
            MenuProps={{
              anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
              transformOrigin: { vertical: 'top', horizontal: 'center' },
              slotProps: {
                paper: {
                  sx: {
                    // a menu surface is the solid popover (Tradehaus .tx-pop), never the 5% tile tint that lets the page show through
                    backgroundColor: 'var(--tx-dialog)',
                    backgroundImage: 'var(--tx-surface-light)',
                    boxShadow: 'var(--tx-shadow-sm)',
                    border: 'none',
                    borderRadius: '12px',
                    mt: '3px',
                    py: '10px',
                    minWidth: 'calc(100vw - 20px) !important',
                    maxWidth: 'calc(100vw - 20px) !important',
                    left: '10px !important',
                  },
                },
              },
            }}
            renderValue={(value) => {
              const selected = TABS.find((t) => t.id === value);
              return (
                <>
                  {selected?.icon}
                  {selected?.mobileLabel ?? selected?.label}
                </>
              );
            }}>
            {TABS.map((t) => (
              <MenuItem
                key={t.id}
                value={t.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: font,
                  fontSize: 14,
                  fontWeight: tab === t.id ? 600 : 400,
                  color: tab === t.id ? C.text : C.muted,
                  backgroundColor: tab === t.id ? 'var(--tx-fill-2)' : 'transparent',
                  borderRadius: '10px',
                  py: '8px',
                  px: '16px',
                  mx: '6px',
                  borderBottom: 0,
                  '&:hover': {
                    backgroundColor:
                      tab === t.id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.10)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'var(--tx-fill-2)',
                    color: C.text,
                    '&:hover': {
                      backgroundColor: 'var(--tx-fill-3)',
                    },
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: 'var(--tx-fill-2)',
                  },
                }}>
                {t.icon}
                {t.mobileLabel ?? t.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      ) : (
        /* Desktop: pill tab bar stretching to download button */
        <Box
          sx={{
            position: 'sticky',
            top: 20,
            zIndex: 10,
            // one 44px row: the tab bar and a square download button the same height beside it
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            mb: '28px',
            maxWidth: `calc(1072px)`,
            height: 44,
          }}>
          <Box
            sx={{
              flex: 1,
              height: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              gap: '2px',
              padding: '4px',
              borderRadius: '12px',
              background: 'var(--tx-glass-panel)',
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow)',
            }}>
            {TABS.map((t) => (
              <Box
                key={t.id}
                onClick={() => setTab(t.id)}
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  padding: '0 4px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: 13,
                  whiteSpace: 'nowrap',
                  // the ds Segmented selection: a tonal tile, ink text at 600; the rest muted at 500
                  fontWeight: tab === t.id ? 600 : 500,
                  color: tab === t.id ? C.text : C.muted,
                  background: tab === t.id ? 'var(--tx-fill-2)' : 'transparent',
                  transition: 'color 0.15s, background 0.15s',
                  fontFamily: font,
                  '&:hover': {
                    background: tab === t.id ? 'var(--tx-fill-2)' : 'var(--tx-fill)',
                    color: C.text,
                  },
                }}>
                <span style={{ display: 'flex', width: 15, flexShrink: 0 }}>{t.icon}</span>
                {t.label}
              </Box>
            ))}
          </Box>

          <Tooltip title="Download as PNG" placement="bottom-end">
            <IconButton
              onClick={handleSnapshot}
              disabled={snapping}
              size="small"
              sx={{
                flexShrink: 0,
                width: 44,
                height: 44,
                p: 0,
                background: 'var(--tx-lime)',
                borderRadius: '12px',
                color: 'var(--tx-on-lime)',
                opacity: snapping ? 0.4 : 1,
                transition: 'opacity 0.15s, background 0.15s',
                '&:hover': { background: 'var(--tx-lime-hover)' },
              }}>
              <DownloadIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Content area + disclaimer — captured together for snapshot */}
      <Box ref={calculatorRef}>
      <Box sx={{ mt: isMobile ? 0 : '35px' }}>
        {tab === 'mortgage' ? (
          <Suspense fallback={null}>
            <MortgageCalculator />
          </Suspense>
        ) : tab === 'affordability' ? (
          <Suspense fallback={null}>
            <AffordabilityCalculator />
          </Suspense>
        ) : tab === 'refinance' ? (
          <Suspense fallback={null}>
            <RefinanceCalculator />
          </Suspense>
        ) : tab === 'rent-vs-buy' ? (
          <Suspense fallback={null}>
            <RentVsBuyCalculator />
          </Suspense>
        ) : tab === 'heloc' ? (
          <Suspense fallback={null}>
            <HELOCCalculator />
          </Suspense>
        ) : tab === 'compound-interest' ? (
          <Suspense fallback={null}>
            <CompoundInterestCalculator />
          </Suspense>
        ) : tab === 'closing-costs' ? (
          <Suspense fallback={null}>
            <ClosingCostCalculator />
          </Suspense>
        ) : (
          <Box
            sx={{
              maxWidth: 800,
              background: C.card,
              backgroundImage: 'var(--tx-surface-light)',
              boxShadow: 'var(--tx-shadow)',
              borderRadius: isMobile ? '16px' : '25px',
              minHeight: isMobile ? undefined : 480,
              overflow: 'hidden',
              padding: isMobile ? '20px 16px 28px' : '28px 24px 36px',
            }}>
            <PlaceholderContent tabId={tab} />
          </Box>
        )}
      </Box>

      {/* Disclaimer footer */}
      <Box
        component="footer"
        className="hx-on-ground"
        sx={{
          px: isMobile ? '10px' : 0,
          py: 3,
          mt: '100px',
          maxWidth: 1000,
          borderTop: '1px solid var(--tx-line)',
          fontSize: 11,
          lineHeight: 1.6,
          color: 'var(--tx-text)',
          textAlign: 'left',
        }}>
        <span style={{ fontWeight: 600 }}>Disclaimer.</span>{' '}
        {
          'The calculators and financial tools provided by Hauser are for informational and educational purposes only. '
        }
        {
          'Results are estimates based on the inputs you provide and do not constitute financial, mortgage, tax, or legal advice. '
        }
        {
          'Actual loan terms, payments, interest rates, and costs will vary based on your creditworthiness, lender requirements, and other factors. '
        }
        {
          'Hauser is not a licensed financial advisor. Nothing on this platform should be relied upon '
        }
        {
          'as a substitute for advice from a qualified mortgage professional, financial advisor, or attorney. '
        }
        {'Always consult with a licensed professional before making any financial decision.'}
      </Box>
      </Box>
    </Box>
  );
};
